import Phaser from 'phaser';
import { BaseScene } from '@core/scenes/BaseScene';
import { Palette } from '@core/config/constants';
import { findCharacter } from '@core/config/characters';
import { AdventureConfig, ADVENTURE_DIFFICULTIES, type AdventureDifficulty } from '@modules/adventure/config';

const PLAYER_TARGET_HEIGHT = 140;
const PLAYER_EMOJI = '🌟';
const PLAYER_FONT_SIZE = '64px';
const ITEM_FONT_SIZE = '56px';

/** Tipos de objeto que pueden caer. */
type ItemKind = 'star' | 'hazard' | 'magnet' | 'shield' | 'life';

const ITEM_EMOJI: Record<ItemKind, string> = {
  star: '⭐',
  hazard: '🌩️',
  magnet: '🧲',
  shield: '🛡️',
  life: '❤️',
};

/** Probabilidad de que un objeto (no peligro) sea un power-up. */
const POWERUP_CHANCE = 0.12;
/** Duracion del iman (ms) y fuerza con la que atrae estrellas (px/s). */
const MAGNET_MS = 6000;
const MAGNET_PULL_SPEED = 640;
/** Radio del escudo dibujado alrededor del jugador. */
const SHIELD_RADIUS = 74;
/** Tope de vidas (para que el HUD de corazones no se desborde). */
const MAX_LIVES = 6;
/** Cada cuanto avanza una oleada (sube velocidad, peligros y ritmo de aparicion). */
const WAVE_DURATION_MS = 13000;

interface FallingItem {
  readonly sprite: Phaser.GameObjects.Text;
  readonly speed: number;
  readonly kind: ItemKind;
}

/**
 * Minijuego de aventura: el jugador mueve su estrella abajo para atrapar estrellas y
 * power-ups (iman, escudo, vida) y esquivar nubes de tormenta. La dificultad escala por
 * oleadas cada cierto tiempo. Control tactil (seguir el dedo) y teclado (flechas).
 */
export class AdventureScene extends BaseScene {
  private score = 0;
  private lives: number = AdventureConfig.startingLives;
  private spawnIntervalMs: number = AdventureConfig.initialSpawnIntervalMs;
  private minSpawnIntervalMs: number = AdventureConfig.minSpawnIntervalMs;
  private fallSpeedMin: number = AdventureConfig.fallSpeedMin;
  private fallSpeedMax: number = AdventureConfig.fallSpeedMax;
  private hazardChance: number = AdventureConfig.hazardChance;
  private isPlaying = false;

  private difficulty: AdventureDifficulty = ADVENTURE_DIFFICULTIES[1];
  private wave = 1;
  private waveTimeLeftMs = 0;

  private magnetMs = 0;
  private shieldActive = false;
  private shieldSprite?: Phaser.GameObjects.Arc;

  private player!: Phaser.GameObjects.Image | Phaser.GameObjects.Text;
  private scoreText!: Phaser.GameObjects.Text;
  private livesText!: Phaser.GameObjects.Text;
  private waveText!: Phaser.GameObjects.Text;
  private statusText!: Phaser.GameObjects.Text;
  private cursors!: Phaser.Types.Input.Keyboard.CursorKeys;
  private items: FallingItem[] = [];
  private timeUntilSpawnMs = 0;

  public constructor() {
    super(AdventureConfig.key);
  }

  public create(): void {
    this.initResponsive();
    this.addGradientBackground(0x0d1b3a, 0x39206b);
    this.addTwinkles(36);
    this.showDifficultyPicker(
      AdventureConfig.title,
      ADVENTURE_DIFFICULTIES,
      (index) => this.beginRound(index),
      this.profile.getHighScore(AdventureConfig.key),
    );
  }

  private beginRound(difficultyIndex: number): void {
    this.difficulty = ADVENTURE_DIFFICULTIES[difficultyIndex];
    this.score = 0;
    this.lives = this.difficulty.startingLives;
    this.minSpawnIntervalMs = this.difficulty.minSpawnIntervalMs;
    this.isPlaying = false;
    this.items = [];
    this.magnetMs = 0;
    this.shieldActive = false;
    this.shieldSprite = undefined;

    this.player = this.createPlayer();
    this.addBackButton();

    this.scoreText = this.add
      .text(this.centerX, 34, '', {
        fontFamily: 'Trebuchet MS, sans-serif',
        fontSize: '34px',
        color: Palette.textLight,
      })
      .setOrigin(0.5, 0);

    this.livesText = this.add
      .text(this.scale.width - 40, 34, '', {
        fontFamily: 'Trebuchet MS, sans-serif',
        fontSize: '34px',
        color: Palette.textLight,
      })
      .setOrigin(1, 0);

    this.waveText = this.add.text(24, 92, '', {
      fontFamily: 'Trebuchet MS, sans-serif',
      fontSize: '22px',
      color: '#4dd0e1',
    });

    this.statusText = this.add
      .text(this.centerX, 74, '', {
        fontFamily: 'Trebuchet MS, sans-serif',
        fontSize: '24px',
        color: '#ffd54f',
      })
      .setOrigin(0.5, 0);

    this.setupInput();
    this.applyWave(1);
    this.waveTimeLeftMs = WAVE_DURATION_MS;
    this.refreshHud();
    this.isPlaying = true;
    this.timeUntilSpawnMs = this.spawnIntervalMs;
  }

  /** Aplica los parametros de la oleada n (escala sobre la dificultad base). */
  private applyWave(n: number): void {
    this.wave = n;
    const step = n - 1;
    this.fallSpeedMin = this.difficulty.fallSpeedMin + 30 * step;
    this.fallSpeedMax = this.difficulty.fallSpeedMax + 40 * step;
    this.hazardChance = Math.min(0.5, this.difficulty.hazardChance + 0.03 * step);
    this.spawnIntervalMs = Math.max(this.minSpawnIntervalMs, this.difficulty.initialSpawnIntervalMs - 70 * step);

    if (n > 1) {
      this.showWaveBanner(n);
    }
    if (this.waveText !== undefined) {
      this.waveText.setText(`Oleada ${n}`);
    }
  }

  private showWaveBanner(n: number): void {
    const banner = this.add
      .text(this.centerX, this.centerY - 40, `¡Oleada ${n}!`, {
        fontFamily: 'Trebuchet MS, sans-serif',
        fontSize: '64px',
        color: Palette.textLight,
        stroke: '#7c4dff',
        strokeThickness: 8,
      })
      .setOrigin(0.5)
      .setAlpha(0)
      .setScale(0.6);

    this.tweens.add({
      targets: banner,
      alpha: { from: 0, to: 1 },
      scale: { from: 0.6, to: 1.1 },
      duration: 320,
      yoyo: true,
      hold: 500,
      ease: 'Back.easeOut',
      onComplete: () => banner.destroy(),
    });
  }

  public update(_time: number, deltaMs: number): void {
    if (!this.isPlaying) {
      return;
    }
    this.handleKeyboardMovement(deltaMs);
    this.handlePowerUps(deltaMs);
    this.handleWaves(deltaMs);
    this.handleSpawning(deltaMs);
    this.handleFalling(deltaMs);
  }

  private createPlayer(): Phaser.GameObjects.Image | Phaser.GameObjects.Text {
    const y = this.scale.height - 90;
    const character = findCharacter(this.profile.character);

    if (character !== undefined) {
      const sprite = this.add.image(this.centerX, y, character.texture).setOrigin(0.5);
      sprite.setScale(PLAYER_TARGET_HEIGHT / sprite.height);
      return sprite;
    }

    return this.add.text(this.centerX, y, PLAYER_EMOJI, { fontSize: PLAYER_FONT_SIZE }).setOrigin(0.5);
  }

  private setupInput(): void {
    this.cursors = this.input.keyboard!.createCursorKeys();
    this.input.on(Phaser.Input.Events.POINTER_MOVE, (pointer: Phaser.Input.Pointer) => {
      if (pointer.isDown || this.input.activePointer.isDown) {
        this.movePlayerTo(pointer.x);
      }
    });
    this.input.on(Phaser.Input.Events.POINTER_DOWN, (pointer: Phaser.Input.Pointer) => {
      this.movePlayerTo(pointer.x);
    });
  }

  private movePlayerTo(x: number): void {
    const margin = AdventureConfig.collisionRadius;
    this.player.x = Phaser.Math.Clamp(x, margin, this.scale.width - margin);
  }

  private handleKeyboardMovement(deltaMs: number): void {
    const distance = (AdventureConfig.playerSpeed * deltaMs) / 1000;
    if (this.cursors.left.isDown) {
      this.movePlayerTo(this.player.x - distance);
    } else if (this.cursors.right.isDown) {
      this.movePlayerTo(this.player.x + distance);
    }
  }

  private handlePowerUps(deltaMs: number): void {
    if (this.magnetMs > 0) {
      this.magnetMs = Math.max(0, this.magnetMs - deltaMs);
    }
    if (this.shieldSprite !== undefined) {
      this.shieldSprite.setPosition(this.player.x, this.player.y);
    }
    this.refreshStatus();
  }

  private handleWaves(deltaMs: number): void {
    this.waveTimeLeftMs -= deltaMs;
    if (this.waveTimeLeftMs <= 0) {
      this.applyWave(this.wave + 1);
      this.waveTimeLeftMs = WAVE_DURATION_MS;
    }
  }

  private handleSpawning(deltaMs: number): void {
    this.timeUntilSpawnMs -= deltaMs;
    if (this.timeUntilSpawnMs > 0) {
      return;
    }
    this.timeUntilSpawnMs = this.spawnIntervalMs;
    this.spawnItem();
  }

  private spawnItem(): void {
    const margin = AdventureConfig.collisionRadius;
    const x = Phaser.Math.Between(margin, this.scale.width - margin);
    const speed = Phaser.Math.Between(this.fallSpeedMin, this.fallSpeedMax);
    const kind = this.pickKind();

    const sprite = this.add.text(x, -40, ITEM_EMOJI[kind], { fontSize: ITEM_FONT_SIZE }).setOrigin(0.5);
    this.items.push({ sprite, speed, kind });
  }

  private pickKind(): ItemKind {
    const roll = Math.random();
    if (roll < this.hazardChance) {
      return 'hazard';
    }
    if (roll < this.hazardChance + POWERUP_CHANCE) {
      const powerRoll = Math.random();
      if (powerRoll < 0.4) {
        return 'magnet';
      }
      if (powerRoll < 0.8) {
        return 'shield';
      }
      return 'life';
    }
    return 'star';
  }

  private handleFalling(deltaMs: number): void {
    const fallDistance = deltaMs / 1000;
    const survivors: FallingItem[] = [];

    for (const item of this.items) {
      item.sprite.y += item.speed * fallDistance;
      this.applyMagnet(item, deltaMs);

      if (this.collidesWithPlayer(item.sprite)) {
        this.resolveCatch(item);
        continue;
      }

      if (item.sprite.y > this.scale.height + 60) {
        item.sprite.destroy();
        continue;
      }

      survivors.push(item);
    }

    this.items = survivors;
  }

  /** Mientras el iman este activo, las estrellas se atraen hacia el jugador. */
  private applyMagnet(item: FallingItem, deltaMs: number): void {
    if (this.magnetMs <= 0 || item.kind !== 'star') {
      return;
    }
    const dx = this.player.x - item.sprite.x;
    const dy = this.player.y - item.sprite.y;
    const distance = Math.hypot(dx, dy) || 1;
    const pull = (MAGNET_PULL_SPEED * deltaMs) / 1000;
    item.sprite.x += (dx / distance) * pull;
    item.sprite.y += (dy / distance) * pull;
  }

  private collidesWithPlayer(sprite: Phaser.GameObjects.Text): boolean {
    const distance = Phaser.Math.Distance.Between(sprite.x, sprite.y, this.player.x, this.player.y);
    return distance < AdventureConfig.collisionRadius;
  }

  private resolveCatch(item: FallingItem): void {
    const { x, y } = item.sprite;
    item.sprite.destroy();

    switch (item.kind) {
      case 'hazard':
        if (this.shieldActive) {
          this.consumeShield();
        } else {
          this.loseLife();
        }
        return;
      case 'magnet':
        this.magnetMs = MAGNET_MS;
        this.audio.playReward();
        this.popText(x, y, '🧲 ¡Imán!');
        return;
      case 'shield':
        this.activateShield();
        this.audio.playReward();
        this.popText(x, y, '🛡️ ¡Escudo!');
        return;
      case 'life':
        this.lives = Math.min(MAX_LIVES, this.lives + 1);
        this.audio.playReward();
        this.popText(x, y, '❤️ +1');
        this.refreshHud();
        return;
      default:
        this.audio.playHit();
        this.score += AdventureConfig.pointsPerStar;
        this.popText(x, y, `+${AdventureConfig.pointsPerStar}`);
        this.refreshHud();
    }
  }

  private activateShield(): void {
    this.shieldActive = true;
    if (this.shieldSprite === undefined) {
      this.shieldSprite = this.add.circle(this.player.x, this.player.y, SHIELD_RADIUS, Palette.accent, 0.2);
      this.shieldSprite.setStrokeStyle(3, Palette.accent, 0.9);
    }
  }

  private consumeShield(): void {
    this.shieldActive = false;
    this.audio.playMiss();
    this.cameras.main.flash(150, 80, 200, 255);
    if (this.shieldSprite !== undefined) {
      this.shieldSprite.destroy();
      this.shieldSprite = undefined;
    }
  }

  private popText(x: number, y: number, text: string): void {
    const popup = this.add
      .text(x, y, text, {
        fontFamily: 'Trebuchet MS, sans-serif',
        fontSize: '26px',
        color: '#ffffff',
        fontStyle: 'bold',
      })
      .setOrigin(0.5);

    this.tweens.add({
      targets: popup,
      y: y - 70,
      alpha: 0,
      duration: 600,
      ease: 'Quad.easeOut',
      onComplete: () => popup.destroy(),
    });
  }

  private loseLife(): void {
    this.lives -= 1;
    this.audio.playWrong();
    this.cameras.main.shake(180, 0.01);
    this.refreshHud();
    if (this.lives <= 0) {
      this.endRound();
    }
  }

  private refreshHud(): void {
    this.scoreText.setText(`Puntos: ${this.score}`);
    this.livesText.setText(`Vidas: ${'❤️'.repeat(Math.max(0, this.lives))}`);
  }

  private refreshStatus(): void {
    const parts: string[] = [];
    if (this.magnetMs > 0) {
      parts.push(`🧲 ${Math.ceil(this.magnetMs / 1000)}s`);
    }
    if (this.shieldActive) {
      parts.push('🛡️');
    }
    this.statusText.setText(parts.join('   '));
  }

  private endRound(): void {
    this.isPlaying = false;
    this.items.forEach((item) => item.sprite.destroy());
    this.items = [];
    if (this.shieldSprite !== undefined) {
      this.shieldSprite.destroy();
      this.shieldSprite = undefined;
    }

    this.profile.recordPlay();
    const isRecord = this.profile.submitScore(AdventureConfig.key, this.score);
    const coins = Math.floor(this.score / 100) * AdventureConfig.coinsPer100Points;
    this.profile.addCoins(coins);

    this.showRoundResults({
      title: isRecord ? '¡Nuevo record!' : '¡Buen intento!',
      score: this.score,
      coins,
      isRecord,
    });
  }
}
